import type { ReactNode } from 'react';

/*
 * A TAP ON THE THUMB'S OWN SPOT RECORDS NOTHING, so the slider also records on
 * release. A range input fires no input or change event when the value does not
 * change, and an untouched detail slider sits at 3 (mood at 0) — so an honest 3
 * saved as blank, and a 0 took two gestures. The pointer or key coming up
 * records whatever the slider now says. Only keys that move it count: Tab
 * arriving on the slider is not an answer.
 */
export const SLIDER_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'PageUp', 'PageDown']);

/**
 * A 0-100 rating that can be "Not said yet".
 *
 * The check-in's mood slider already works this way: null until it is touched,
 * recorded on input, on release and on a key that moves it. A worksheet drafted
 * from a journal entry needs the same thing for a feeling he named without a
 * strength and a belief he never rated — those used to sit at 60 and 70 and be
 * saved as his (13 Sep). With no value the thumb is hollow, parked in the
 * middle, and the scale says so.
 */
export function RatingSlider({
  id,
  value,
  onChange,
  low,
  high,
  suffix = '',
  after,
  min = 0,
  max = 100,
  step = 5,
}: {
  id: string;
  value: number | null;
  onChange: (value: number) => void;
  low: string;
  high: string;
  suffix?: string;
  /** Shown after the value once there is one — e.g. how far it dropped. */
  after?: ReactNode;
  min?: number;
  max?: number;
  step?: number;
}): JSX.Element {
  const parked = Math.round((min + max) / 2 / step) * step;
  return (
    <>
      <input
        id={id}
        className={value === null ? 'slider slider-unset' : 'slider'}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value ?? parked}
        aria-valuetext={value === null ? 'not said yet' : `${value}${suffix}`}
        onChange={(e) => onChange(Number(e.target.value))}
        onPointerUp={(e) => onChange(Number(e.currentTarget.value))}
        onKeyUp={(e) => {
          if (SLIDER_KEYS.has(e.key)) onChange(Number(e.currentTarget.value));
        }}
      />
      <div className="slider-scale">
        <span className="faint">{low}</span>
        {value === null ? (
          <strong className="muted">Not said yet</strong>
        ) : (
          <strong>
            {value}
            {suffix}
            {after}
          </strong>
        )}
        <span className="faint">{high}</span>
      </div>
    </>
  );
}
